import React, { useCallback, useEffect, useMemo, useState } from "react";
import { isEqual } from "lodash";
import {
  Button,
  Group,
  LoadingOverlay,
  Stack,
  TextInput,
  NumberInput,
  Select,
  Textarea,
  Modal,
  ColorInput,
  Title,
} from "@mantine/core";
import { useNavigate, useLocation } from "react-router-dom";
import { useDebug } from "common/debug/hooks/useDebug";
import Collapsable from "common/admin/ui/Collapsable";
import { ADMIN_ORGANIZATION, ORGANIZATIONS } from "_roles";
import { useOrganization } from "common/admin/auth/hooks";
import { AccessRights } from "common/admin/auth/AccessRights";
import { Sonometer } from "sonometer/types/sonometer.model";
import { PopupType } from "sonometer/types"; // Import PopupType
import { SonometerService } from "services/sonometer.service";
import { SonometerTestComponent } from "./SonometerTestComponent";
import { useListChampionshipsQuery } from "championship/api/championship.api";
import { Championship } from "championship/types/championship.model";
import FileInput from "common/admin/ui/FileInput";
import { SonometerBreadcrumbs } from "../SonometerBreadcrumbs";
import ControlPanel from "sonometer/components/ControlPanel";
import { SONOMETER_PATH, SONOMETER_SCREEN_PATH } from "_urls";

const sonometerService = new SonometerService();

export function SonometerAdminForm({
  sonometer,
  editMode,
  onSonometerUpdated,
}: {
  sonometer?: Sonometer;
  editMode: boolean;
  onSonometerUpdated?: (updatedSonometer: Sonometer) => void;
}) {
  const { log } = useDebug();

  const [opened, setOpened] = useState(false);

  const navigate = useNavigate();
  const [organization] = useOrganization();
  const location = useLocation();
  const isDuplicate = location.pathname.endsWith("/duplicate");

  const [isLoading, setIsLoading] = useState(false);

  const isCreateMode = !sonometer || !editMode;

  const getTitle = () => {
    if (isCreateMode) return "New Sonometer";
    return sonometer?.opponentName || "Sonometer";
  };

  const {
    data: championships,
    isLoading: isLoadingChampionships,
    error: championshipsError,
  } = useListChampionshipsQuery();

  const championshipOptions = useMemo(() => {
    if (!championships) {
      return [];
    }
    return championships.map((championship: Championship) => ({
      value: championship.id,
      label: championship.name,
    }));
  }, [championships]);

  // No longer need useEffect for RTK Query errors

  const getInitialValues = useCallback(() => {
    if (sonometer && (editMode || isDuplicate)) {
      return {
        id: isDuplicate ? undefined : sonometer.id, // Clear ID for duplication
        partitionId: sonometer.partitionId || "",
        parentId: isDuplicate ? sonometer.id : undefined, // Set parentId for duplication
        opponentName: isDuplicate
          ? `${sonometer.opponentName} (copy)`
          : sonometer.opponentName || "",
        championshipId: sonometer.championshipId || "", // Clear championshipId for duplication
        score: isDuplicate ? 0 : sonometer.score || 0, // Reset score for duplication
        createdAt: isDuplicate
          ? new Date().toISOString()
          : sonometer.createdAt || new Date().toISOString(),
        owner: {
          L1: sonometer.owner?.L1 || organization || "", // Duplicate L1
          L2: sonometer.owner?.L2 || "", // Duplicate L2
          L3: sonometer.owner?.L3 || "", // Duplicate L3
          L4: sonometer.owner?.L4 || "", // Duplicate L4
        },
        caliberationOffset: sonometer.caliberationOffset || 138.0,
        publicScreenSettings: sonometer.publicScreenSettings || {
          welcomeMessage: "",
          teaserMessage: "",
          endMessage: "",
          backgroundColor: "",
          backgroundImage: "",
          textColor: "",
          textSize: 0,
          sponsorLogo: "",
          sponsorMargin: "",
          welcomeGif: "",
          teaserGif: "",
          endGif: "",
        },
        screen: sonometer.screen || ("texte1" as PopupType), // Add screen property and cast default
        animations: sonometer.animations || [], // Duplicate animations
      };
    }

    return {
      partitionId: "",
      opponentName: "",
      championshipId: "",
      score: 0,
      createdAt: new Date().toISOString(),
      owner: {
        L1: organization || "",
        L2: "",
        L3: "",
        L4: "",
      },
      caliberationOffset: 138.0,
      publicScreenSettings: {
        welcomeMessage: "",
        teaserMessage: "",
        endMessage: "",
        backgroundColor: "",
        backgroundImage: "",
        textColor: "",
        textSize: 0,
        sponsorLogo: "",
        sponsorMargin: "",
        welcomeGif: "",
        teaserGif: "",
        endGif: "",
      },
      screen: "texte1" as PopupType, // Default value for new sonometer, explicitly cast
      animations: [],
    };
  }, [sonometer, editMode, isDuplicate, organization]);

  // Define a type for the form values to ensure 'screen' is PopupType
  type SonometerFormValues = Omit<Sonometer, "id"> & { id?: string };

  const [formValues, setFormValues] =
    useState<SonometerFormValues>(getInitialValues());

  useEffect(() => {
    setFormValues(getInitialValues());
  }, [getInitialValues, sonometer, isDuplicate]);

  const handlePublicScreenSettingsChange = (field: string, value: any) => {
    setFormValues((prev) => ({
      ...prev,
      publicScreenSettings: {
        ...prev.publicScreenSettings,
        [field]: value,
      },
    }));
  };

  const openScreencast = () => {
    const sonometerId = sonometer?.id;
    if (sonometerId && editMode) {
      const screencastUrl = `/${SONOMETER_SCREEN_PATH}/${SONOMETER_PATH}/${sonometerId}`;
      window.open(
        screencastUrl,
        "_blank",
        "width=1540,height=220,resizable=yes,scrollbars=yes"
      );
    }
  };

  const isDirty = useMemo(
    () => !isEqual(formValues, getInitialValues()),
    [formValues, getInitialValues]
  );

  const handleInputChange = (field: string, value: any) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOwnerChange = (field: string, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      owner: {
        ...prev.owner,
        [field]: value,
      },
    }));
  };

  const save = async () => {
    setIsLoading(true);

    // Basic validation
    if (!formValues.opponentName.trim()) {
      log("Validation failed: opponentName is required");
      setIsLoading(false);
      return;
    }

    if (!formValues.championshipId.trim()) {
      log("Validation failed: championshipId is required");
      setIsLoading(false);
      return;
    }

    try {
      let resultSonometer: Sonometer;
      if (sonometer && editMode && !isDuplicate) {
        // Extract id from formValues to prevent it from overriding sonometer.id
        const { id, ...restFormValues } = formValues;
        resultSonometer = await sonometerService.updateSonometer(
          sonometer.id,
          restFormValues
        );
        if (onSonometerUpdated) {
          const latestSonometer = await sonometerService.getSonometerById(
            sonometer.id
          );
          onSonometerUpdated(latestSonometer);
        }
      } else {
        resultSonometer = await sonometerService.addSonometer(formValues);
        navigate(`../../`);
      }
      log("Sonometer saved successfully", resultSonometer);
    } catch (error) {
      log("Error saving sonometer", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSonometer = async (id: string) => {
    try {
      await sonometerService.removeSonometer(id);
      console.log(`Sonometer ${id} deleted successfully`);
      setOpened(false);
      navigate("/admin/sonometers");
    } catch (error) {
      console.error("Failed to delete Sonometer", error);
    }
  };

  return (
    <>
      {isLoading && <LoadingOverlay visible={true} overlayOpacity={0.8} />}
      <Stack>
        <div
          className="sticky top-0 z-50 pb-4"
          style={{ backgroundColor: "#E9ECEF" }}
        >
          <Title order={3} align="center" m="xs">
            {getTitle()}
          </Title>
          <SonometerBreadcrumbs sonometer={isCreateMode ? "new" : sonometer} />
          <Group position="right">
            <Button
              disabled={!isDirty && !isDuplicate}
              onClick={save}
              className="bg-button-admin disabled:opacity-75"
            >
              Save
            </Button>
          </Group>
        </div>

        <Stack>
          <Group className="grid grid-cols-2">
            {organization === ADMIN_ORGANIZATION && (
              <AccessRights
                authorizedOrganizations={[ADMIN_ORGANIZATION]}
                authorizedRoles={["Admin"]}
              >
                <Select
                  sx={{ flex: 1 }}
                  label="Organization"
                  required
                  data={ORGANIZATIONS}
                  value={formValues.owner.L1}
                  onChange={(value) => handleOwnerChange("L1", value ?? "")}
                />
              </AccessRights>
            )}
            <TextInput
              label="Opponent Name"
              required
              value={formValues.opponentName}
              onChange={(e) =>
                handleInputChange("opponentName", e.target.value)
              }
              placeholder="Enter opponent name"
            />
          </Group>

          <Group className="grid grid-cols-2">
            <Select
              label="Championship"
              required
              data={championshipOptions}
              value={formValues.championshipId}
              onChange={(value) =>
                handleInputChange("championshipId", value ?? "")
              }
              placeholder="Select championship"
              searchable
              nothingFound="No championships found"
              disabled={isLoadingChampionships}
              error={
                championshipsError ? "Failed to load championships" : undefined
              }
            />
            <NumberInput
              step={0.1}
              precision={1}
              label="Score"
              value={formValues.score}
              onChange={(value) => handleInputChange("score", value || 0)}
              min={0}
              placeholder="Enter initial score"
            />
          </Group>
        </Stack>

        <Collapsable title="Microphone Settings" defaultOpened={true}>
          <NumberInput
            label="Caliberation Offset"
            value={formValues.caliberationOffset}
            onChange={(value) => handleInputChange("caliberationOffset", value)}
            min={0}
            step={0.1}
            precision={1}
            placeholder="Enter caliberation offset"
          />
        </Collapsable>

        <Collapsable title="Public Screen Settings" defaultOpened={false}>
          <Stack>
            <Textarea
              label="Welcome Message"
              value={formValues.publicScreenSettings.welcomeMessage || ""}
              onChange={(e) =>
                handlePublicScreenSettingsChange(
                  "welcomeMessage",
                  e.target.value
                )
              }
              placeholder="Enter welcome message"
            />
            <Textarea
              label="Teaser Message"
              value={formValues.publicScreenSettings.teaserMessage || ""}
              onChange={(e) =>
                handlePublicScreenSettingsChange(
                  "teaserMessage",
                  e.target.value
                )
              }
              placeholder="Enter teaser message"
            />
            <Textarea
              label="End Message"
              value={formValues.publicScreenSettings.endMessage || ""}
              onChange={(e) =>
                handlePublicScreenSettingsChange("endMessage", e.target.value)
              }
              placeholder="Enter end message"
            />
            <Group className="grid grid-cols-2">
              <ColorInput
                label="Background Color"
                value={formValues.publicScreenSettings.backgroundColor || ""}
                onChange={(value) =>
                  handlePublicScreenSettingsChange("backgroundColor", value)
                }
                placeholder="Choose background color"
              />
              <ColorInput
                label="Text Color"
                value={formValues.publicScreenSettings.textColor || ""}
                onChange={(value) =>
                  handlePublicScreenSettingsChange("textColor", value)
                }
                placeholder="Choose text color"
              />
              {/* <TextInput
                label="Background Image URL"
                value={formValues.publicScreenSettings.backgroundImage || ""}
                onChange={(e) =>
                  handlePublicScreenSettingsChange(
                    "backgroundImage",
                    e.target.value
                  )
                }
                placeholder="Enter background image URL"
              /> */}

              <FileInput
                label="Sponsor Logo URL"
                value={formValues.publicScreenSettings.sponsorLogo || ""}
                onChange={(value) =>
                  handlePublicScreenSettingsChange("sponsorLogo", value)
                }
              />
              <FileInput
                label="Gauge Image URL"
                value={formValues.publicScreenSettings.gaugeImage || ""}
                onChange={(value) =>
                  handlePublicScreenSettingsChange("gaugeImage", value)
                }
              />
              <FileInput
                label="Welcome Animated GIF"
                value={formValues.publicScreenSettings.welcomeGif || ""}
                onChange={(value) =>
                  handlePublicScreenSettingsChange("welcomeGif", value)
                }
              />
              <FileInput
                label="Teaser Animated GIF"
                value={formValues.publicScreenSettings.teaserGif || ""}
                onChange={(value) =>
                  handlePublicScreenSettingsChange("teaserGif", value)
                }
              />
              <FileInput
                label="End Screen Animated GIF"
                value={formValues.publicScreenSettings.endGif || ""}
                onChange={(value) =>
                  handlePublicScreenSettingsChange("endGif", value)
                }
              />
              <TextInput
                label="Text Size"
                value={
                  formValues.publicScreenSettings.textSize?.toString() || ""
                }
                onChange={(e) =>
                  handlePublicScreenSettingsChange("textSize", e.target.value)
                }
                placeholder="Enter text size"
              />
              {/* <TextInput
                label="Sponsor Margin"
                value={formValues.publicScreenSettings.sponsorMargin || ""}
                onChange={(e) =>
                  handlePublicScreenSettingsChange(
                    "sponsorMargin",
                    e.target.value
                  )
                }
                placeholder="Enter sponsor margin"
              /> */}
            </Group>
          </Stack>
        </Collapsable>

        <Collapsable title="Animations" defaultOpened={false}>
          <Textarea
            label="Animations (JSON)"
            value={JSON.stringify(formValues.animations, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                handleInputChange("animations", parsed);
              } catch (error) {
                // Keep as string if invalid JSON
                console.error("Invalid JSON");
              }
            }}
            minRows={6}
            placeholder="Enter animations as JSON"
          />
        </Collapsable>

        {sonometer && editMode && !isDuplicate && (
          <Group position="center">
            <Button
              className="bg-red-600 w-auto"
              onClick={() => setOpened(true)}
            >
              Delete
            </Button>
          </Group>
        )}

        <Modal
          opened={opened}
          onClose={() => setOpened(false)}
          centered
          radius={0}
          overflow="inside"
          withCloseButton={false}
        >
          <p className="text-xl">Are you sure to delete this sonometer?</p>
          <div className="flex gap-40 mt-6">
            <Button
              style={{
                backgroundColor: "#e30613",
                color: "white",
              }}
              onClick={() => handleDeleteSonometer(sonometer!.id)}
              className="w-24"
            >
              Yes
            </Button>
            <Button
              style={{ color: "white" }}
              onClick={() => setOpened(false)}
              className="w-24 bg-button-admin"
            >
              No
            </Button>
          </div>
        </Modal>

        {/* Control Panel Section */}
        {editMode && !isDuplicate && (
          <>
            <Collapsable title="Control Panel">
              <Group spacing="md" mb="md">
                <Button className="bg-green-500" onClick={openScreencast}>
                  Open Screencast
                </Button>
              </Group>
              <ControlPanel />
            </Collapsable>
          </>
        )}

        {/* Test Component */}
        <Collapsable title="🚀 Test High Score Update" defaultOpened={true}>
          <SonometerTestComponent
            currentSonometer={
              sonometer
                ? {
                    ...sonometer,
                    ...formValues,
                    id: sonometer.id,
                  }
                : undefined
            }
          />
        </Collapsable>
      </Stack>
    </>
  );
}
